import { AHSeparator, AHTeamIcon, AHText, Box, useLayoutContext } from "arkh";
import React, { useCallback, useMemo } from "react";
import { FlatList, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { usePaginationFragment, graphql, useLazyLoadQuery } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { TeamIcon } from "./__generated__/RoutineDetailScreenTopTaskIssueCreator_Routine.graphql";

import { TaskAllIssuesScreenQuery } from "./__generated__/TaskAllIssuesScreenQuery.graphql";
import { RoutineDetailNavigatorScreenProps } from "./RoutineDetailStack";

import { TaskAllIssuesScreenTodoList_todos$key } from "./__generated__/TaskAllIssuesScreenTodoList_todos.graphql";

import { TodoListItem } from "@/src/components/TodoListItem";

function TaskAllIssuesTodoList(props: {
  task: TaskAllIssuesScreenTodoList_todos$key;
  onTodoEdit: (fromResponse: boolean, todoId: string) => void;
}) {
  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment TaskAllIssuesScreenTodoList_todos on Task
      @refetchable(queryName: "TaskAllIssuesListTodoQuery") {
        todos(first: $first, after: $after, sectionId: $sectionId)
          @connection(key: "TaskAllIssuesListTodo_todos") {
          edges {
            node {
              id
              categoryId
              ...TodoListItem_todo
            }
          }
        }
      }
    `,
    props.task
  );

  const { bottom } = useSafeAreaInsets();
  const { space } = useLayoutContext();
  const containerStyle = useMemo(
    () => ({
      paddingHorizontal: space.medium,
      paddingTop: space.medium,
      paddingBottom: space.medium + bottom,
    }),
    [bottom, space.medium]
  );

  const { onTodoEdit } = props;
  const openTodoEdit = useCallback(
    (fromResponse: boolean, todoId: string) => {
      onTodoEdit(fromResponse, todoId);
    },
    [onTodoEdit]
  );

  return (
    <FlatList
      ItemSeparatorComponent={AHSeparator}
      data={data.todos.edges}
      onEndReached={() => {
        if (hasNext && !isLoadingNext) {
          loadNext(10);
        }
      }}
      contentContainerStyle={containerStyle}
      renderItem={({ item, index }) => {
        return (
          <TodoListItem
            isFirst={index === 0}
            isLast={data.todos.edges.length - 1 === index}
            openTodo={openTodoEdit}
            todo={item.node}
          />
        );
      }}
    />
  );
}

function TaskAllIssues(props: {
  taskId: string;

  fetchKey: string;
  section: {
    id: string;
    title: string;
    icon: TeamIcon | null;
  } | null;
  onTodoEdit: (fromResponse: boolean, todoId: string) => void;
}) {
  const data = useLazyLoadQuery<TaskAllIssuesScreenQuery>(
    graphql`
      query TaskAllIssuesScreenQuery(
        $taskId: ID!
        $sectionId: ID
        $first: Int
        $after: ID
      ) {
        task(id: $taskId) {
          ...TaskAllIssuesScreenTodoList_todos
        }
      }
    `,
    {
      taskId: props.taskId,
      sectionId: props.section?.id ?? undefined,
      first: 10,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.task) {
    return null;
  }
  return (
    <View style={{ flex: 1 }}>
      {props.section ? (
        <Box
          color="surface"
          spaceBottom="medium"
          spaceTop="xxsmall"
          borderColor="border-primary"
          style={{
            borderBottomWidth: 1,
            justifyContent: "center",
          }}
        >
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {props.section.icon ? (
              <AHTeamIcon
                color="darkText"
                size="xxsmall"
                type={props.section.icon}
              />
            ) : null}
            <AHText type="subtitle" spaceLeft="xxsmall" numberOfLines={1}>
              {props.section.title}
            </AHText>
          </View>
        </Box>
      ) : null}

      <TaskAllIssuesTodoList task={data.task} onTodoEdit={props.onTodoEdit} />
    </View>
  );
}

export function TaskAllIssuesScreen(
  props: RoutineDetailNavigatorScreenProps<"TaskAllIssues">
) {
  const onTodoEdit = useCallback(
    (fromResponse: boolean, todoId: string) => {
      props.navigation.navigateDeprecated("TodoEdit", {
        autofocusResponse: fromResponse,
        todoId,
      });
    },
    [props.navigation]
  );
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {useCallback(
        (fetchKey) => (
          <TaskAllIssues
            taskId={props.route.params.taskId}
            section={props.route.params.section ?? null}
            fetchKey={fetchKey}
            onTodoEdit={onTodoEdit}
          />
        ),
        [onTodoEdit, props.route.params.section, props.route.params.taskId]
      )}
    </LazyQueryNetworkBoundrary>
  );
}
